import { FC, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { getSpacingToken, SpacingToken, Stack, Tag, TagProps, TagVariant } from '@optimus-web/core';
import { TooltippedTag } from '../tooltip';

interface TagObject extends Pick<TagProps, 'leadingIcon' | 'trailingIcon' | 'variant'> {
    value: React.ReactNode;
}

type Tag = React.ReactNode | TagObject;

interface TagListProps {
    tags: Tag[];
    vertical?: boolean;
    compact?: boolean;
    spacing?: SpacingToken;
    /**
     * common variant for all tags. If tag has it's own variant set it overwrites this global variant
     */
    tagsVariant?: TagVariant;
}

const MAX_TAG_WIDTH = 200;

export const TagList: FC<TagListProps> = ({ tags, spacing = '100', compact, vertical, tagsVariant }) => {
    const [restCount, setRestCount] = useState<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const tagSizesRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!compact || !wrapperRef.current || !wrapperRef.current.parentElement) {
            return;
        }

        const dimensionKey = vertical ? 'height' : 'width';
        const { children } = wrapperRef.current;
        let tagsSize = 0;
        const wrapperSize = wrapperRef.current.parentElement.getBoundingClientRect()[dimensionKey];
        const spacingBetweenTags = getSpacingToken(spacing)({ theme });
        let localRestCount = 0;

        for (let i = 0; i < children.length; i++) {
            const isLast = i === children.length - 1;
            const tagSize = children[i].getBoundingClientRect()[dimensionKey] + (isLast ? 0 : spacingBetweenTags);
            tagSizesRef.current = { ...tagSizesRef.current, [i]: tagSize };

            if (tagsSize + tagSize > wrapperSize) {
                localRestCount = tags.length - i + 1;
                break;
            }
            tagsSize += tagSize;
        }

        setRestCount(localRestCount);
    }, [compact, spacing, tags.length, theme, vertical]);

    const finalTags = restCount ? tags.slice(0, tags.length - restCount) : tags;

    // hide the tags until rest count is calculated (only for compact variant)
    const style = compact && restCount === null ? { opacity: '0' } : {};

    return (
        <Stack wrap={!compact} spacing={spacing} vertical={vertical} itemAlignment="start" ref={wrapperRef}>
            {finalTags.map((tag, index) => {
                const key = `tag-${index}`;
                const isTagOverflowing = tagSizesRef.current[index] > MAX_TAG_WIDTH;

                if (!isTagObject(tag)) {
                    return (
                        <div style={style} key={key}>
                            {isTagOverflowing ? (
                                <TooltippedTag tooltip={tag} variant={tagsVariant}>
                                    {tag}
                                </TooltippedTag>
                            ) : (
                                <Tag variant={tagsVariant}>{tag}</Tag>
                            )}
                        </div>
                    );
                }
                const { value, ...others } = tag;
                return (
                    <div style={style} key={key}>
                        {isTagOverflowing ? (
                            <TooltippedTag {...others} tooltip={value} variant={others.variant ?? tagsVariant}>
                                {value}
                            </TooltippedTag>
                        ) : (
                            <Tag {...others} variant={others.variant ?? tagsVariant}>
                                {value}
                            </Tag>
                        )}
                    </div>
                );
            })}

            {!!restCount && <Tag variant={tagsVariant}>+{restCount}</Tag>}
        </Stack>
    );
};

const isTagObject = (tag: Tag): tag is TagObject =>
    !!(tag && typeof tag === 'object' && 'value' in tag && tag.value !== undefined);
